import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import { CenterSpinner } from "~/atoms/spinner";
import { AdminConsentCard } from "~/molecules/onboard-microsoft/AdminConsentCard";
import { LegacyMigrationCard } from "~/molecules/onboard-microsoft/LegacyMigrationCard";
import { SignInCard } from "~/molecules/onboard-microsoft/SignInCard";
import { SpeSetupCard } from "~/molecules/onboard-microsoft/SpeSetupCard";
import {
  OnboardingSidebar,
  getCurrentStep,
  type StepId,
} from "~/molecules/onboard-microsoft/OnboardingSidebar";
import { LanguageSelector } from "~/molecules/LanguageSelector";
import { useMicrosoftIntegration } from "~/api/v2/useMicrosoftIntegration";

export default function MicrosoftOnboardingDashboard() {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const setupToken = searchParams.get("token");

  const { data, isLoading } = useMicrosoftIntegration(setupToken, {
    refreshInterval: 5000,
  });

  const status = searchParams.get("status");
  const [showErrorDialog, setShowErrorDialog] = useState(status === "error");
  const [currentStep, setCurrentStep] = useState<StepId | null>(null);

  if (!setupToken) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!data) {
    return (
      <div className="grid h-screen place-items-center">
        {t(($) => $.msIntegration.tokenNotFound)}
      </div>
    );
  }

  const activeStep = currentStep ?? getCurrentStep(data);

  const handleSkipUserMigration = () => {
    setCurrentStep("speSetup");
  };

  return (
    <>
      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(($) => $.msIntegration.error.title)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(($) => $.msIntegration.error.description)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>
              {t(($) => $.commons.buttons.confirm)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="container flex min-h-screen flex-col items-center space-y-8 px-4 py-6">
        <div className="flex w-full justify-end">
          <LanguageSelector />
        </div>

        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold">
            {t(($) => $.msIntegration.title)}
          </h1>
          <p className="text-muted-foreground">
            {t(($) => $.msIntegration.subtitle)}
          </p>
        </div>

        {/* Main content with sidebar */}
        <div className="relative flex justify-center">
          {/* Sidebar */}
          <aside className="absolute top-0 right-full hidden w-70 pr-8 md:block">
            <div className="sticky top-8">
              <OnboardingSidebar
                data={data}
                currentStep={activeStep}
                onStepClick={setCurrentStep}
              />
            </div>
          </aside>

          {/* Main content area */}
          <main className="w-180 min-w-0">
            {activeStep === "adminConsent" && (
              <AdminConsentCard data={data} setupToken={setupToken} />
            )}

            {activeStep === "userMigration" &&
              data.hasLegacyProvider &&
              (data.hasAccessToken ? (
                <LegacyMigrationCard
                  setupToken={setupToken}
                  onSkip={handleSkipUserMigration}
                />
              ) : (
                <SignInCard
                  setupToken={setupToken}
                  onSkip={handleSkipUserMigration}
                />
              ))}

            {activeStep === "speSetup" && (
              <SpeSetupCard data={data} setupToken={setupToken} />
            )}
          </main>
        </div>
      </div>
    </>
  );
}
